import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';
import NotificationHelpModal from '../Admin/NotificationHelpModal';
import NotificationSendNowModal from './NotificationSendNowModal';

/**
 * Admin → Notifications → Recipients
 * Feature-first roster (who receives what, on what schedule) + per-user editor.
 * Users can always opt out from Profile → Notification Settings.
 */
const NotificationRecipientsPage = ({ embedded = false }) => {
  const { instance, accounts } = useMsal();
  const { viewers, isConnected, connectionState } = usePresence('notificationRecipients', 'global', true);

  const [roster, setRoster] = useState([]);
  const [loadingRoster, setLoadingRoster] = useState(true);
  const [expandedFeature, setExpandedFeature] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [sendFeature, setSendFeature] = useState(null);

  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const [selectedUser, setSelectedUser] = useState(null);
  const [userFeatures, setUserFeatures] = useState([]);
  const [loadingUser, setLoadingUser] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const [error, setError] = useState(null);

  const searchBoxRef = useRef(null);

  const fetchRoster = useCallback(async () => {
    try {
      setLoadingRoster(true);
      const res = await authenticatedApiCall(instance, accounts, 'get', '/notification-preferences/admin/roster');
      setRoster(res.data.features || []);
    } catch (err) {
      setError('Failed to load roster: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoadingRoster(false);
    }
  }, [instance, accounts]);

  useEffect(() => { fetchRoster(); }, [fetchRoster]);

  useEffect(() => {
    if (!userSearch.trim()) { setUserResults([]); return; }
    const t = setTimeout(async () => {
      try {
        setSearching(true);
        const res = await authenticatedApiCall(
          instance, accounts, 'get',
          `/notification-preferences/admin/users?search=${encodeURIComponent(userSearch.trim())}`
        );
        setUserResults(res.data.users || []);
        setShowResults(true);
      } catch (err) {
        console.error('User search failed:', err);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [userSearch, instance, accounts]);

  useEffect(() => {
    const handler = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setShowResults(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadUser = useCallback(async (user) => {
    setSelectedUser(user);
    setShowResults(false);
    setUserSearch('');
    setUserResults([]);
    try {
      setLoadingUser(true);
      const res = await authenticatedApiCall(instance, accounts, 'get', `/notification-preferences/admin/user/${user.id}`);
      setUserFeatures(res.data.features || []);
    } catch (err) {
      setError('Failed to load user preferences: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoadingUser(false);
    }
  }, [instance, accounts]);

  const setOverride = useCallback(async (feature, subscribed) => {
    if (!selectedUser) return;
    setSavingKey(feature);
    try {
      await authenticatedApiCall(instance, accounts, 'put', '/notification-preferences/admin/override', {
        userId: selectedUser.id, feature, subscribed
      });
      setUserFeatures(prev => prev.map(f => f.feature === feature ? { ...f, subscribed, adminManaged: false } : f));
      fetchRoster();
    } catch (err) {
      setError('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingKey(null);
    }
  }, [instance, accounts, selectedUser, fetchRoster]);

  const clearOverride = useCallback(async (feature, userId = null) => {
    const targetId = userId || selectedUser?.id;
    if (!targetId) return;
    setSavingKey(feature + (userId || ''));
    try {
      await authenticatedApiCall(instance, accounts, 'delete', '/notification-preferences/admin/override', {
        userId: targetId, feature
      });
      if (!userId && selectedUser) {
        const meta = userFeatures.find(f => f.feature === feature);
        const def = meta?.defaultSubscribed !== false;
        setUserFeatures(prev => prev.map(f => f.feature === feature ? { ...f, subscribed: def, adminManaged: false } : f));
      }
      fetchRoster();
    } catch (err) {
      setError('Failed to clear: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingKey(null);
    }
  }, [instance, accounts, selectedUser, fetchRoster, userFeatures]);

  const card = {
    background: '#fff', border: '1px solid #e9ecef', borderRadius: '8px',
    padding: '12px 14px', marginBottom: 0,
  };

  const cronFeatures = roster.filter(f => f.trigger === 'cron');
  const otherFeatures = roster.filter(f => f.trigger !== 'cron');

  const modeMeta = (f) => {
    const mode = f.deliveryMode
      || (f.defaultSubscribed === false ? 'managed_list' : f.canUnsubscribe === false ? 'required' : 'auto_audience');
    if (mode === 'managed_list') {
      return {
        mode,
        badge: 'Managed list',
        badgeBg: '#e7f1ff',
        badgeColor: '#0b5ed7',
        countLabel: f.receivingCount === 0 ? 'Empty list' : `${f.receivingCount} on list`,
        countColor: f.receivingCount === 0 ? '#b45309' : '#334155',
      };
    }
    if (mode === 'required') {
      return {
        mode,
        badge: 'Required',
        badgeBg: '#f1f5f9',
        badgeColor: '#475569',
        countLabel: 'Must send',
        countColor: '#64748b',
      };
    }
    const muted = f.mutedCount || 0;
    return {
      mode,
      badge: 'Auto audience',
      badgeBg: '#ecfdf5',
      badgeColor: '#047857',
      countLabel: muted > 0 ? `${muted} muted in Profile` : 'Picked at send time',
      countColor: muted > 0 ? '#b45309' : '#64748b',
    };
  };

  const FeatureBlock = ({ f }) => {
    const open = expandedFeature === f.feature;
    const m = modeMeta(f);
    const muted = f.mutedRecipients || [];
    return (
      <div style={{ borderBottom: '1px solid #f0f0f0' }}>
        <div style={{ display: 'flex', alignItems: 'stretch' }}>
        <button
          type="button"
          onClick={() => setExpandedFeature(open ? null : f.feature)}
          style={{
            flex: 1, minWidth: 0, textAlign: 'left', display: 'grid',
            gridTemplateColumns: 'minmax(180px, 1.4fr) minmax(140px, 0.9fr) minmax(160px, 1.4fr) 110px 130px 18px',
            alignItems: 'center', gap: 10, padding: '8px 12px',
            border: 'none', background: open ? '#f8f9fa' : '#fff', cursor: 'pointer',
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 13, color: '#2c3e50', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.label}>
            {f.label}
          </div>
          <div style={{ fontSize: 11, color: '#6c757d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.schedule}>
            {f.schedule}
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.audienceHint || ''}>
            {f.audienceHint || '—'}
          </div>
          <span style={{
            justifySelf: 'start', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
            background: m.badgeBg, color: m.badgeColor, whiteSpace: 'nowrap',
          }}>
            {m.badge}
          </span>
          <span style={{ fontSize: 12, color: m.countColor, fontWeight: 500, textAlign: 'right', whiteSpace: 'nowrap' }}>
            {m.countLabel}
          </span>
          <span style={{ color: '#adb5bd', fontSize: 12 }}>{open ? '▾' : '▸'}</span>
        </button>
        <button
          type="button"
          onClick={() => setSendFeature(f)}
          style={{
            border: 'none', borderLeft: '1px solid #f0f0f0',
            background: open ? '#f8f9fa' : '#fff', color: '#0f766e',
            fontWeight: 700, fontSize: 12, padding: '0 12px', cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >Send now</button>
        </div>
        {open && (
          <div style={{ padding: '8px 12px 10px', background: '#fafbfc', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <p style={{ margin: '0 0 6px', fontSize: 12, color: '#6c757d', lineHeight: 1.4 }}>{f.description}</p>
              {m.mode === 'auto_audience' && (
                <div style={{ fontSize: 12, color: '#065f46', lineHeight: 1.4 }}>
                  <strong>Auto audience</strong> — emails go to the “Who” group at send time. This panel only lists Profile mutes.
                </div>
              )}
              {m.mode === 'managed_list' && (
                <div style={{ fontSize: 12, color: '#084298', lineHeight: 1.4 }}>
                  <strong>Managed list</strong> — only listed people get this email (unless muted in Profile). Add via the right panel.
                </div>
              )}
              {m.mode === 'required' && (
                <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.4 }}>
                  <strong>Required</strong> — must reach the person involved; no Profile mute.
                </div>
              )}
            </div>
            <div>
              {m.mode === 'managed_list' && (
                f.recipients.length === 0 ? (
                  <div style={{ fontSize: 12, color: '#b45309' }}>List empty — add people in the right panel.</div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {f.recipients.map(r => (
                      <span key={r.userId} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        fontSize: 11, padding: '3px 8px', borderRadius: 999,
                        background: '#fff', border: '1px solid #dee2e6', color: '#334155',
                      }} title={r.email}>
                        {r.displayName || r.email}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); clearOverride(f.feature, r.userId); }}
                          disabled={savingKey === f.feature + r.userId}
                          style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 12, padding: 0, lineHeight: 1 }}
                          title="Remove"
                        >×</button>
                      </span>
                    ))}
                  </div>
                )
              )}
              {m.mode === 'auto_audience' && (
                muted.length === 0 ? (
                  <div style={{ fontSize: 12, color: '#64748b' }}>No Profile mutes.</div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {muted.map(r => (
                      <span key={r.userId} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        fontSize: 11, padding: '3px 8px', borderRadius: 999,
                        background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412',
                      }} title={r.email}>
                        {r.displayName || r.email}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); clearOverride(f.feature, r.userId); }}
                          disabled={savingKey === f.feature + r.userId}
                          style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#c2410c', fontSize: 11, padding: 0 }}
                          title="Clear mute"
                        >unmute</button>
                      </span>
                    ))}
                  </div>
                )
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const listHeader = (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'minmax(180px, 1.4fr) minmax(140px, 0.9fr) minmax(160px, 1.4fr) 110px 130px 18px',
      gap: 10, padding: '6px 12px', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0',
      fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3,
    }}>
      <div>Notification</div>
      <div>When</div>
      <div>Who</div>
      <div>Mode</div>
      <div style={{ textAlign: 'right' }}>Status</div>
      <div />
    </div>
  );

  const helpBtn = (
    <button
      type="button"
      onClick={() => setShowHelp(true)}
      title="How Notifications work"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '6px 12px', borderRadius: 6, border: '1px solid #99f6e4',
        background: '#f0fdfa', color: '#0f766e', fontSize: 13, fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      ❓ Help
    </button>
  );

  return (
    <div style={{ padding: embedded ? 0 : '16px 20px', width: '100%', maxWidth: 'none', boxSizing: 'border-box' }}>
      <div style={{
        marginBottom: 12, display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
      }}>
        <div style={{ minWidth: 0, flex: '1 1 320px' }}>
          {!embedded ? (
            <>
              <h1 style={{ margin: 0, fontSize: 20, color: '#2c3e50' }}>Notification Recipients</h1>
              <p style={{ margin: '2px 0 0', color: '#6c757d', fontSize: 13 }}>
                Who gets which email, and when. Mute available under Profile → Notification Settings.
              </p>
            </>
          ) : (
            <p style={{ margin: 0, color: '#6c757d', fontSize: 13 }}>
              Who gets which email, and when. Mute available under Profile → Notification Settings.
            </p>
          )}
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          fontSize: 11, color: '#64748b',
        }}>
          <span style={{ fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.3 }}>Modes</span>
          <span style={{ fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: '#e7f1ff', color: '#0b5ed7' }}>Managed list</span>
          <span>= you pick people</span>
          <span style={{ fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: '#ecfdf5', color: '#047857' }}>Auto audience</span>
          <span>= job picks at send</span>
          <span style={{ fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: '#f1f5f9', color: '#475569' }}>Required</span>
          <span>= must send</span>
          {helpBtn}
          {!embedded && (
            <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={3} showConnectionStatus={true} />
          )}
        </div>
      </div>

      {error && (
        <div style={{ background: '#f8d7da', border: '1px solid #f5c6cb', color: '#721c24', padding: '10px 12px', borderRadius: 6, marginBottom: 12 }}>
          {error}
          <button onClick={() => setError(null)} style={{ float: 'right', border: 'none', background: 'none', cursor: 'pointer', color: '#721c24' }}>&times;</button>
        </div>
      )}

      <div
        className="notif-recipients-layout"
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(280px, 340px)',
          gap: 14,
          alignItems: 'start',
        }}
      >
        <div style={{ display: 'grid', gap: 12, minWidth: 0 }}>
          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
              <h3 style={{ margin: 0, fontSize: 14 }}>Scheduled</h3>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>{cronFeatures.length} emails · click a row for details</span>
            </div>
            {loadingRoster ? (
              <div style={{ padding: 16, color: '#6c757d' }}>Loading…</div>
            ) : (
              <div style={{ border: '1px solid #e9ecef', borderRadius: 6, overflow: 'hidden' }}>
                {listHeader}
                {cronFeatures.map(f => <FeatureBlock key={f.feature} f={f} />)}
              </div>
            )}
          </div>

          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
              <h3 style={{ margin: 0, fontSize: 14 }}>Event &amp; other</h3>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>{otherFeatures.length} emails</span>
            </div>
            {loadingRoster ? null : (
              <div style={{ border: '1px solid #e9ecef', borderRadius: 6, overflow: 'hidden' }}>
                {listHeader}
                {otherFeatures.map(f => <FeatureBlock key={f.feature} f={f} />)}
              </div>
            )}
          </div>
        </div>

        <div style={{ ...card, position: 'sticky', top: 8, maxHeight: 'calc(100vh - 160px)', overflow: 'auto' }}>
          <h3 style={{ margin: '0 0 10px', fontSize: 14 }}>Add or edit a user</h3>
          <div ref={searchBoxRef} style={{ position: 'relative' }}>
            <input
              type="text"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              onFocus={() => userResults.length && setShowResults(true)}
              placeholder="Search name or email…"
              style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #ced4da', fontSize: 13, boxSizing: 'border-box' }}
            />
            {showResults && (userResults.length > 0 || searching) && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
                background: '#fff', border: '1px solid #ced4da', borderRadius: 6,
                marginTop: 4, maxHeight: 220, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}>
                {searching && <div style={{ padding: '10px 12px', color: '#6c757d', fontSize: 13 }}>Searching…</div>}
                {!searching && userResults.map(u => (
                  <div
                    key={u.id}
                    onClick={() => loadUser(u)}
                    style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', fontSize: 13 }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f8f9fa'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
                  >
                    <div style={{ fontWeight: 500, color: '#2c3e50' }}>{u.display_name || u.email}</div>
                    <div style={{ fontSize: 12, color: '#6c757d' }}>{u.email}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedUser && (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: '#2c3e50', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedUser.display_name || selectedUser.email}</div>
                  <div style={{ color: '#6c757d', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedUser.email}</div>
                </div>
                <button onClick={() => { setSelectedUser(null); setUserFeatures([]); }} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#6c757d', fontSize: 12, flexShrink: 0 }}>Clear</button>
              </div>

              {loadingUser ? (
                <div style={{ padding: 12, color: '#6c757d', fontSize: 13 }}>Loading…</div>
              ) : (
                <div style={{ border: '1px solid #e9ecef', borderRadius: 6, overflow: 'hidden' }}>
                  {userFeatures.map(f => (
                    <div key={f.feature} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '7px 10px', borderBottom: '1px solid #f0f0f0',
                      opacity: savingKey === f.feature ? 0.6 : 1, gap: 8,
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 500, fontSize: 12, color: '#2c3e50', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.label}>{f.label}</div>
                        <div style={{ fontSize: 10, color: '#adb5bd', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.schedule}</div>
                      </div>
                      <div style={{ flexShrink: 0 }}>
                        {!f.canUnsubscribe ? (
                          <span style={{ fontSize: 10, color: '#6c757d', padding: '3px 6px', background: '#f0f0f0', borderRadius: 4 }}>Required</span>
                        ) : (
                          <button
                            onClick={() => setOverride(f.feature, !f.subscribed)}
                            disabled={savingKey === f.feature}
                            title={f.subscribed ? 'On list / receiving — click to stop' : 'Not receiving — click to enable'}
                            style={{
                              width: 42, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer',
                              backgroundColor: f.subscribed ? '#28a745' : '#dc3545', position: 'relative', padding: 0
                            }}
                          >
                            <div style={{
                              width: 18, height: 18, borderRadius: '50%', background: '#fff',
                              position: 'absolute', top: 2, left: f.subscribed ? 22 : 2,
                              transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                            }} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {!selectedUser && (
            <p style={{ margin: '12px 0 0', fontSize: 12, color: '#94a3b8', lineHeight: 1.4 }}>
              Search a person to add them to a managed list or clear a mute.
            </p>
          )}
        </div>
      </div>

      {showHelp && <NotificationHelpModal onClose={() => setShowHelp(false)} />}
      {sendFeature && (
        <NotificationSendNowModal
          feature={sendFeature}
          instance={instance}
          accounts={accounts}
          onClose={() => setSendFeature(null)}
        />
      )}

      <style>{`
        @media (max-width: 1100px) {
          .notif-recipients-layout { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default NotificationRecipientsPage;
